import { type MeResponse, type Permission } from '@platform/contracts';
import {
  ErrorState,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
} from '@platform/ui';
import { type ReactNode, useId, useState } from 'react';

import { errorMessage } from '../lib/api';
import { useMe } from '../lib/queries';
import { tenantsInScope } from '../lib/scope';
import { useScope } from '../lib/use-scope';

/** Esqueleto de carga de una lista/tabla. */
export function ListSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div className="flex flex-col gap-2" aria-busy="true" aria-label="Cargando">
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} className="h-14 w-full" />
      ))}
    </div>
  );
}

/** Carga / error con reintento / contenido. */
export function Loadable({
  isLoading,
  error,
  onRetry,
  skeleton,
  children,
}: {
  isLoading: boolean;
  error: unknown;
  onRetry?: () => void;
  skeleton?: ReactNode;
  children: ReactNode;
}) {
  if (isLoading) return <>{skeleton ?? <ListSkeleton />}</>;
  if (error) return <ErrorState message={errorMessage(error)} {...(onRetry ? { onRetry } : {})} />;
  return <>{children}</>;
}

type ScopedTenant = MeResponse['clients'][number]['tenants'][number] & {
  clientId: string;
  clientSlug: string;
};

/**
 * Pantallas que trabajan sobre UNA empresa (prompts, config): candidatas = empresas del alcance
 * con el permiso; si hay varias, un selector local (el alcance del header sigue mandando).
 */
export function useTenantPick(permission: Permission) {
  const me = useMe();
  const { scope } = useScope();
  const candidates: ScopedTenant[] = tenantsInScope(me, scope).filter(
    (t) => me.user.isSuperadmin || t.permissions.includes(permission),
  );
  const [picked, setPicked] = useState<string | undefined>(undefined);
  const tenant = candidates.find((t) => t.id === picked) ?? candidates[0];
  const can = (p: Permission) =>
    tenant ? me.user.isSuperadmin || tenant.permissions.includes(p) : false;
  return { tenant, candidates, setPicked, can };
}

export function TenantPicker({
  candidates,
  value,
  onChange,
}: {
  candidates: readonly { id: string; name: string }[];
  value: string | undefined;
  onChange: (id: string) => void;
}) {
  const id = useId();
  if (candidates.length < 2) return null;
  return (
    <div className="flex w-full max-w-xs flex-col gap-1.5">
      <Label htmlFor={id}>Empresa</Label>
      <Select value={value ?? ''} onValueChange={onChange}>
        <SelectTrigger id={id}>
          <SelectValue placeholder="Elige una empresa" />
        </SelectTrigger>
        <SelectContent>
          {candidates.map((t) => (
            <SelectItem key={t.id} value={t.id}>
              {t.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
